import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { MagVoice } from '../components/mag-voice.js';
import { TopNav } from '../components/top-nav.js';
import type { Brand, FeedCard, FeedCardKind } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { formatDate, formatRelativeTimeCompact, relativeDayWord } from '../lib/format.js';
import { brandsQuery, feedQuery, meQuery } from '../lib/queries.js';

// /brand/$slug/library — the archive of everything mag has written
// for the brand. P7 of the agency home design doc (CommitMind).
//
// Reads the same per-brand feed endpoint /home reads (no separate
// archive endpoint yet — the feed already returns the full set, the
// home just shows a curated slice). Renders cards grouped by month,
// newest first, with a search input that filters by title + body.
//
// Pure archive — no in-section decisions or receipts here. Founder
// clicks a row to drop into the existing /feed#cardId surface where
// each card kind has its own detail view. P9 inverts the
// relationship: /feed redirects here, /library becomes canonical.
// Until then both URLs work and this page is opt-in via the home
// footer's "all reads →" link.

export const Route = createFileRoute('/brand/$slug/library')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    await queryClient.ensureQueryData(feedQuery(brand.id));
    return { brand };
  },
  component: LibraryPage,
});

function LibraryPage() {
  const { t } = useLingui();

  const { brand } = Route.useRouteContext();
  useBrandTitle(brand.slug, ['library']);
  const [query, setQuery] = useState('');
  const { data } = useQuery(feedQuery(brand.id));
  const cards = data?.cards ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(
      (c) => c.title.toLowerCase().includes(q) || c.body.toLowerCase().includes(q),
    );
  }, [cards, query]);

  const groups = useMemo(() => groupByMonth(filtered), [filtered]);

  return (
    <div className="min-h-dvh bg-[var(--cream)]">
      <TopNav active="home" />
      <main className="mx-auto max-w-[680px] px-4 pb-20 pt-8 sm:px-6">
        <header className="border-y border-[var(--line)] py-6">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>library · everything i've written for you</Trans>
          </p>
          <MagVoice
            as="p"
            className="mt-3 max-w-[480px] text-[15px] leading-snug text-[var(--ink)]/85"
          >
            <Trans>
              home keeps the asks + receipts current. this is the archive — every brief, drop,
              recap, and read i've sent. search it when you're trying to remember what i said about
              something.
            </Trans>
          </MagVoice>
        </header>

        <div className="mt-6">
          <label className="block">
            <span className="sr-only">
              <Trans>search the library</Trans>
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t`search by word…`}
              className="w-full rounded-md border border-[var(--line)] bg-white px-3 py-2 font-display text-[13.5px] text-[var(--ink)] outline-none transition-colors duration-150 placeholder:text-[var(--ink)]/45 focus:border-[var(--plum)]"
            />
          </label>
        </div>

        {groups.length === 0 ? (
          <LibraryEmpty hasQuery={query.trim().length > 0} />
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            {groups.map((g) => (
              <LibraryGroup key={g.key} brand={brand} group={g} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function LibraryEmpty({ hasQuery }: { hasQuery: boolean }) {
  return (
    <div className="mt-10 border-t border-[var(--line)] py-10 text-center">
      <MagVoice as="p" className="text-[16px] leading-snug text-[var(--ink)]/85">
        {hasQuery ? (
          <Trans>nothing matched — try a different word.</Trans>
        ) : (
          <Trans>the library's empty for now. give me a week of working and this fills up.</Trans>
        )}
      </MagVoice>
    </div>
  );
}

function LibraryGroup({ brand, group }: { brand: Brand; group: MonthGroup }) {
  return (
    <section className="border-t border-[var(--line)] pt-4">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {group.label}
      </p>
      <ul className="mt-3 flex flex-col">
        {group.cards.map((card, idx) => (
          <li
            key={card.id}
            className="mag-stagger-in"
            style={{ animationDelay: `${Math.min(idx, 8) * 30}ms` }}
          >
            <Link
              to="/brand/$slug/home"
              params={{ slug: brand.slug }}
              search={{ card: card.id }}
              className={`group flex items-baseline justify-between gap-3 px-1 py-2 no-underline transition-colors duration-150 hover:bg-[var(--paper)] ${idx > 0 ? 'border-t border-[var(--line)]/60' : ''}`}
            >
              <span className="flex min-w-0 flex-1 items-baseline gap-2">
                <span className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
                  {kindAbbrev(card.kind)}
                </span>
                <span className="min-w-0 flex-1 truncate font-display text-[13.5px] leading-tight text-[var(--ink)]">
                  {card.title.toLowerCase()}
                </span>
              </span>
              <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55 transition-colors group-hover:text-[var(--ink)]/80">
                {formatLibraryDate(card.createdAt)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface MonthGroup {
  key: string; // YYYY-MM
  label: string;
  cards: FeedCard[];
}

function groupByMonth(cards: FeedCard[]): MonthGroup[] {
  const byKey = new Map<string, MonthGroup>();
  // Cards arrive from the server newest-first; preserve that order
  // within each month group.
  for (const c of cards) {
    const d = new Date(c.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = formatDate(d, { month: 'long', year: 'numeric' }).toLowerCase();
    let g = byKey.get(key);
    if (!g) {
      g = { key, label, cards: [] };
      byKey.set(key, g);
    }
    g.cards.push(c);
  }
  // Newest month first.
  return [...byKey.values()].sort((a, b) => b.key.localeCompare(a.key));
}

/** Compact 3-4 char kind tag for the row's leading mono label. Skips
 *  the verbose enum names so the row's eye-line lands on the title.
 *  Unknown kinds fall through to the first 4 chars of the enum so
 *  new kinds work without a wiring step. */
/** OUR feed-card enum, abbreviated for the founder to read — so it
 *  gets a translated label. */
function kindAbbrev(kind: FeedCardKind): string {
  switch (kind) {
    case 'monday_recap':
      return i18n._(msg`recap`);
    case 'friday_brief':
      return i18n._(msg`brief`);
    case 'creative_drop':
      return i18n._(msg`drop`);
    case 'meta_engagement_digest':
      return i18n._(msg`digest`);
    case 'meta_history_learn':
      return i18n._(msg`read`);
    case 'meta_content_plan':
      return i18n._(msg`plan`);
    case 'monthly_plan':
      return i18n._(msg`plan`);
    case 'brand_evolution':
    case 'brand_evolution_auto_applied':
      return i18n._(msg`brand`);
    case 'autopilot_weekly_recap':
      return i18n._(msg`wrap`);
    case 'campaign_rollup':
      return i18n._(msg`rollup`);
    case 'concept_proposal':
    case 'creative_variants':
    case 'strategy_proposal':
    case 'mag_campaign_proposal':
    case 'mag_channel_proposal':
      return i18n._(msg`ask`);
    case 'analytics_finding':
      return i18n._(msg`find`);
    case 'campaign_insight':
      return i18n._(msg`read`);
    case 'campaign_nudge':
    case 'analytics_install_nudge':
      return i18n._(msg`nudge`);
    case 'mag_chat':
      return i18n._(msg`chat`);
    case 'mag_followup':
    case 'mag_followup_complete':
      return i18n._(msg`todo`);
    case 'trust_migration_proposal':
      return i18n._(msg`trust`);
    case 'cross_channel_budget_proposal':
    case 'paid_strategy_refresh_proposal':
    case 'attribution_policy_proposal':
      return i18n._(msg`ask`);
    case 'policy_issue':
      return i18n._(msg`policy`);
    case 'welcome':
      return i18n._(msg`hello`);
    default:
      return kind.slice(0, 4);
  }
}

function formatLibraryDate(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 1) return relativeDayWord(0);
  if (diffDays < 2) return relativeDayWord(-1);
  if (diffDays < 30) return formatRelativeTimeCompact(d);
  return formatDate(d, { month: 'short', day: 'numeric' }).toLowerCase();
}
